import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { LiliToolTipColor, LiliTooltip } from '../../../../DesignSystem/LiliTooltip/LiliTooltip';
import { ReactComponent as SyncIcon } from '../../../../assets/icons/svg/shared/ic-shared-sync.svg';
import { FEATURE_FLAG } from '../../../../constants/general';
import { PlanUpgradeFlow } from '../../../../flows/planUpgrade';
import Button from '../../../../generic-components/Button';
import Heading from '../../../../generic-components/Heading';
import { useUpdateInAppComponent } from '../../../../hooks/mutations/useUpdateInAppComponentsLogs';
import { InAppComponent } from '../../../../hooks/query/useGetInappComponentsLogs';
import { useGetSingleInAppComponentLog } from '../../../../hooks/utilities/useGetSingleInAppComponentLog';
import { VIEWS_MAP } from '../../../../router/constants';
import { validateFeatureFlag } from '../../../../services/featureFlagService';
import { useStore } from '../../../../store';
import { useLinkedAccounts } from '../../hooks/useLinkedAccounts';
import SyncCardModal from './SyncCardModal';
import { SyncYourCardPromotionalModal } from './SyncYourCardPromotionalModal';

const MAX_TOOL_TIP_COUNT = 3;
const TOOLTIP_DURATION_MS = 8000;
const SyncYourCardButtonAndModals = () => {
  const { t } = useTranslation();
  const { data: linkedAccounts = [] } = useLinkedAccounts();
  const { customerId } = useStore((state) => state.user);
  const setShowSyncCardModal = useStore((state) => state.setShowSyncCardModal);
  const [showSyncCardPromotionalModal, setShowSyncCardPromotionalModal] = useState(false);
  const [isPlansPopupPresented, setIsPlansPopupPresented] = useState(false);
  const [isTooltipOpen, setIsTooltipOpen] = useState(false);
  const { mutateAsync: updateInAppComponentLogMutation } = useUpdateInAppComponent();
  const navigate = useNavigate();
  const isSmartOrPremium = validateFeatureFlag(FEATURE_FLAG.SYNC_CARD);
  const updateTooltipCount = (presentedCount: number) => {
    updateInAppComponentLogMutation({
      customerId,
      body: {
        name: InAppComponent.SYNC_YOUR_CARD_FIRST_TIME,
        doNotShowAgain: presentedCount >= MAX_TOOL_TIP_COUNT,
      },
    });
  };
  const startTooltipCountDown = (presentedCount: number) => {
    updateTooltipCount(presentedCount);
    const timeoutId = setTimeout(() => setIsTooltipOpen(false), TOOLTIP_DURATION_MS);
    return () => clearTimeout(timeoutId);
  };

  const { data: appComponentLog, isLoading } = useGetSingleInAppComponentLog(
    customerId,
    InAppComponent.SYNC_YOUR_CARD_FIRST_TIME,
  );

  useEffect(() => {
    if (linkedAccounts.length === 0 && !isLoading && isSmartOrPremium) {
      if (appComponentLog) {
        if (appComponentLog.presentedCount < MAX_TOOL_TIP_COUNT) {
          setIsTooltipOpen(true);
          startTooltipCountDown(appComponentLog.presentedCount || 0);
        }
      } else {
        setIsTooltipOpen(true);
        startTooltipCountDown(0);
      }
    }
  }, [appComponentLog, customerId, isLoading]);

  const handleSyncYourCardButtonClick = () => {
    if (isSmartOrPremium) {
      setShowSyncCardModal(true);
    } else {
      setShowSyncCardPromotionalModal(true);
    }
  };

  return (
    <>
      {linkedAccounts.length === 0 && (
        <div className='flex flex-row items-center'>
          <LiliTooltip
            description={t('TRANSACTIONS_FACELIFT.SYNC_CARD.TOOLTIP')}
            targetId='sync-card-button-tool-tip'
            color={LiliToolTipColor.Blue}
            isOpen={isTooltipOpen}
            showCloseButton
            onClose={() => setIsTooltipOpen(false)}
            width='315px'
          />
          <div data-tooltip-id='sync-card-button-tool-tip'>
            <Button
              className='hover:bg-bw02 hover:text-action03 rounded-[4px]'
              id='syncCardButton'
              onClick={handleSyncYourCardButtonClick}
              childElement={(
                <div className='flex justify-center gap-2 px-[20px] py-[9px]'>
                  <SyncIcon />
                  <Heading
                    title={t('TRANSACTIONS_FACELIFT.SYNC_CARD.TITLE')}
                    className='font-metricR text-[14px] text-center'
                  />
                </div>
              )}
            />
          </div>

          <div className='w-[1px] h-[12px] bg-bw04' />
        </div>
      )}
      <SyncCardModal />
      {isPlansPopupPresented && (
        <PlanUpgradeFlow
          onCloseClick={() => {
            setIsPlansPopupPresented(false);
            navigate(`/${VIEWS_MAP.DASHBOARD}`);
          }}
          origin='Plan Promotion'
          promotionType='Sync Credit Card Feature'
        />
      )}
      {showSyncCardPromotionalModal && (
        <SyncYourCardPromotionalModal
          onClose={() => setShowSyncCardPromotionalModal(false)}
          onUpgrade={() => setIsPlansPopupPresented(true)}
        />
      )}
    </>
  );
};

export default SyncYourCardButtonAndModals;
